/**
 * JSON-LD structured data builders (plan/08 §3). Emitted via
 * `<script type="application/ld+json">` in `<svelte:head>`: Organization +
 * WebSite + SoftwareApplication on `/` only; FAQPage on `/faq` only — never
 * also on `/`, since a duplicate FAQPage across URLs is a flag, even though the
 * homepage shows an inline subset of the same questions. BreadcrumbList on
 * every other public page (lib/seo/Meta.svelte) and Article on the dated
 * buying-question pages (ArticlePage.svelte) and the dated guides.
 *
 * The three site-wide entities carry stable `@id`s so the nodes on different
 * pages join into one graph (the app's publisher is the organization, the
 * articles' author is the same organization) for search engines and the AI
 * answer engines that read structured data.
 */
import { env } from '$env/dynamic/public';
import { site, company, meta, microcopy } from '$lib/content/site';
import { pricing } from '$lib/content/pricing';
import { craftTools } from '$lib/content/features';
import { platforms } from '$lib/content/requirements';
import type { Faq } from '$lib/content/faq';

export function siteUrl(): string {
	return (env.PUBLIC_SITE_URL || site.url).replace(/\/$/, '');
}

const id = {
	organization: () => `${siteUrl()}/#organization`,
	website: () => `${siteUrl()}/#website`,
	software: () => `${siteUrl()}/#software`
};

/**
 * A reference to the organization that still stands on its own: a page that
 * does not carry the full Organization node (everything but `/`) gets its
 * type, name and URL too, not a bare `@id` a consumer cannot resolve.
 */
function organizationRef(): Record<string, unknown> {
	return {
		'@type': 'Organization',
		'@id': id.organization(),
		name: company.legalName,
		url: siteUrl()
	};
}

/** Organization details backed by the Estonian e-Business Register. */
export function organizationJsonLd(): Record<string, unknown> {
	const url = siteUrl();
	return {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		'@id': id.organization(),
		name: company.legalName,
		description: `The company behind ${site.name}, PPF pattern software. ${microcopy.studioLine}`,
		brand: { '@type': 'Brand', name: site.name },
		email: site.contactEmail,
		contactPoint: {
			'@type': 'ContactPoint',
			contactType: 'sales',
			email: site.contactEmail,
			url: `${url}/ppf-pattern-software#demo`
		},
		identifier: company.registryCode,
		vatID: company.vatNumber,
		address: {
			'@type': 'PostalAddress',
			streetAddress: 'Luha tn 2-6',
			postalCode: '50703',
			addressLocality: 'Tartu',
			addressCountry: 'EE'
		},
		url,
		// Reuses the apple-touch-icon (scripts/favicons.mjs) rasterized from the
		// same app icon as the favicon — a real, already-generated asset rather
		// than a new one, and a raster mark is the safer choice for structured-
		// data logo consumers than the SVG.
		logo: `${url}/apple-touch-icon.png`
	};
}

/** WebSite — names the site and ties it to its publisher. */
export function websiteJsonLd(): Record<string, unknown> {
	return {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		'@id': id.website(),
		name: site.name,
		url: siteUrl(),
		inLanguage: 'en',
		publisher: organizationRef()
	};
}

/** One requirements row for every platform, e.g. "macOS 12 Monterey; Windows 10 64-bit". */
function requirement(label: string, field: 'min' | 'rec' = 'min'): string {
	return platforms
		.map((p) => p.rows.find((r) => r.label === label)?.[field])
		.filter((v, i, all) => v && all.indexOf(v) === i)
		.join('; ');
}

/**
 * SoftwareApplication — plan/08 §3's shape, plus what an answer engine needs
 * to describe the product without guessing: who it is for, what it does, and
 * what it runs on (all interpolated from the content modules). `priceValidUntil` is
 * intentionally omitted: `pricing.deliveryWindow` ("December 2026") is a delivery
 * estimate, not a decided price-validity end-date, and isn't a valid ISO 8601
 * date — inventing one would be worse than leaving the (optional) field out.
 */
export function softwareApplicationJsonLd(): Record<string, unknown> {
	return {
		'@context': 'https://schema.org',
		'@type': 'SoftwareApplication',
		'@id': id.software(),
		name: site.name,
		applicationCategory: 'DesignApplication',
		applicationSubCategory: 'PPF pattern software',
		operatingSystem: platforms.map((p) => `${p.name} (${p.arch})`).join(', '),
		softwareRequirements: requirement('System')
			.split('; ')
			.map((system) => `${system} or later`)
			.join('; '),
		memoryRequirements: `${requirement('Memory')} minimum, ${requirement('Memory', 'rec')} recommended`,
		storageRequirements: `${requirement('Storage')} minimum, ${requirement('Storage', 'rec')} recommended`,
		description: meta.homeDescription,
		url: siteUrl(),
		image: `${siteUrl()}/og.png`,
		screenshot: `${siteUrl()}/img/real-preflight.webp`,
		audience: {
			'@type': 'BusinessAudience',
			audienceType: 'Paint protection film installation shops and independent installers'
		},
		featureList: [
			'Imports 3D scan meshes: STL, OBJ and PLY',
			'Flattens the scanned panel and maps stretch and compression before cutting',
			...craftTools.map((t) => `${t.title}: ${t.line}`),
			'Exports SVG, DXF and PLT (HP-GL), full scale in millimetres'
		],
		publisher: organizationRef(),
		offers: {
			'@type': 'Offer',
			price: String(pricing.founderYear),
			priceCurrency: pricing.currency,
			description: `Presale: 12 months of access at ${pricing.discountPct}% off`,
			availability: 'https://schema.org/PreOrder',
			url: `${siteUrl()}/pricing`,
			seller: organizationRef()
		}
	};
}

/** FAQPage — /faq only (see file header). */
export function faqPageJsonLd(faqs: Faq[]): Record<string, unknown> {
	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqs.map((f) => ({
			'@type': 'Question',
			name: f.q,
			acceptedAnswer: { '@type': 'Answer', text: f.a }
		}))
	};
}

/** BreadcrumbList — home, then the page. Every public page but `/` (Meta.svelte). */
export function breadcrumbJsonLd(path: string, name: string): Record<string, unknown> {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{ '@type': 'ListItem', position: 1, name: site.name, item: siteUrl() },
			{ '@type': 'ListItem', position: 2, name, item: `${siteUrl()}${path}` }
		]
	};
}

/**
 * Article — the buying-question pages (ArticlePage.svelte), which print the
 * date their facts were last checked. That date is `dateModified`: freshness
 * is the signal that matters on a comparison or compatibility page.
 */
export function articleJsonLd(a: {
	path: string;
	headline: string;
	description: string;
	/** Guides that keep their first publication date (ArticlePage pages only track freshness). */
	datePublished?: string;
	dateModified: string;
	image: string;
}): Record<string, unknown> {
	const url = `${siteUrl()}${a.path}`;
	return {
		'@context': 'https://schema.org',
		'@type': 'Article',
		headline: a.headline,
		description: a.description,
		...(a.datePublished ? { datePublished: a.datePublished } : {}),
		dateModified: a.dateModified,
		image: `${siteUrl()}${a.image}`,
		url,
		mainEntityOfPage: url,
		inLanguage: 'en',
		author: organizationRef(),
		publisher: organizationRef(),
		about: { '@type': 'SoftwareApplication', '@id': id.software(), name: site.name, url: siteUrl() }
	};
}

/**
 * Serializes a JSON-LD object into a literal `<script>` tag string for
 * `{@html}` insertion in `<svelte:head>`. Escapes `<` so the payload can never
 * prematurely close the tag — defense in depth; every input above is
 * first-party content-module data, never user input.
 */
export function jsonLdScriptTag(data: unknown): string {
	const json = JSON.stringify(data).replace(/</g, '\\u003c');
	return `<script type="application/ld+json">${json}</script>`;
}
